/**
 * /pipeline-facility/tickets — the PF field ticket register
 * (GET /pipeline-facility/tickets, fieldiq-api#1227, PF-4).
 *
 * A ticket is drafted from its job's daily logs (job → Tickets → Create
 * ticket), so this register has no New button; it says where tickets come
 * from. The status filter lives in the URL. Totals are the API's: a draft has
 * no snapshot total yet, so it reads `—` rather than a figure nobody approved.
 */
import React from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ClipboardList, FileCheck2, Send } from 'lucide-react';
import { PageHeaderBand } from '../../../components/layout/PageHeaderBand';
import { displayValue } from '../../../features/fieldiq-handoff/contracts';
import { usePfMeta } from '../../../hooks/usePipelineFacility';
import { usePfTickets } from '../../../hooks/usePfTickets';
import { fmtCurrency } from '../../../lib/display-labels';
import { queryLoadFailure } from '../../../lib/load-failure';
import { pfScopeText, pfTicketStatusSkin, PF_TICKET_STATUS_SKIN } from '../../../lib/pf-tickets';
import type { PfTicket } from '../../../types/pf-tickets';
import { useOpenRecord } from '../../downhole/recordPage';
import { PF_EYEBROW, PfBadge, PfChip, PfChipRow, PfEmpty, PfPanel, PfRegister, type PfColumn } from '../PfBits';

/** The figure a register row shows: the office snapshot once signed, the priced-lines sum never (a list row has no lines). */
export const pfTicketTotalText = (t: PfTicket) => fmtCurrency(t.totalAmount, t.currency);

export default function PfTicketsScreen() {
  const navigate = useNavigate();
  const openRecord = useOpenRecord();
  const [searchParams, setSearchParams] = useSearchParams();
  const status = searchParams.get('status') ?? '';
  const setStatus = (next: string) => {
    const p = new URLSearchParams(searchParams);
    if (next) p.set('status', next);
    else p.delete('status');
    setSearchParams(p, { replace: true });
  };
  const catalog = usePfMeta().data?.data ?? null;
  const listQuery = usePfTickets(status ? { status } : undefined);
  const allQuery = usePfTickets();
  const tickets = listQuery.data?.data ?? [];
  const all = allQuery.isLoading || queryLoadFailure(allQuery) ? undefined : allQuery.data?.data;
  const countOf = (pick: (t: PfTicket) => boolean) => displayValue(all ? all.filter(pick).length : undefined);
  const statuses = catalog?.ticketStatuses?.length
    ? catalog.ticketStatuses
    : Object.entries(PF_TICKET_STATUS_SKIN).map(([value, s]) => ({ value, label: s.label }));

  const columns: PfColumn<PfTicket>[] = [
    { key: 'number', header: 'Ticket', render: (t) => <span className="font-mono font-extrabold text-white">{t.ticketNumber || '—'}</span> },
    { key: 'job', header: 'Job', render: (t) => (<><p className="font-mono">{t.jobNumber || '—'}</p><p className="mt-0.5 text-xs text-fieldiq-muted">{t.siteName || '—'}</p></>) },
    { key: 'customer', header: 'Customer', render: (t) => t.customerName || '—' },
    { key: 'scope', header: 'Covers', render: (t) => pfScopeText(t) },
    { key: 'status', header: 'Status', render: (t) => <PfBadge skin={pfTicketStatusSkin(t.status)} value={t.status} /> },
    { key: 'total', header: 'Total', align: 'right', render: (t) => <span className="font-mono">{pfTicketTotalText(t)}</span> },
  ];

  return (
    <div data-testid="pf-tickets-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={PF_EYEBROW}
        title="Field Tickets"
        description="Tickets drafted from the daily logs, approved by the office and signed by the company man."
        onBack={() => navigate(-1)}
        backLabel="Back"
        stats={[
          { icon: <ClipboardList className="size-4" aria-hidden="true" />, label: 'Drafts', tone: 'neutral', loading: allQuery.isLoading, value: countOf((t) => t.status === 'draft') },
          { icon: <Send className="size-4" aria-hidden="true" />, label: 'With the customer', tone: 'accent', loading: allQuery.isLoading, value: countOf((t) => t.status === 'office_approved') },
          { icon: <FileCheck2 className="size-4" aria-hidden="true" />, label: 'Customer approved', tone: 'success', loading: allQuery.isLoading, value: countOf((t) => t.status === 'customer_approved') },
        ]}
      />
      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          <p className="text-sm text-fieldiq-muted">
            A ticket is drafted from its job: open the <Link className="underline" to="/pipeline-facility/jobs">job</Link>, then Tickets → Create ticket.
          </p>
          <PfChipRow label="Filter by status">
            <PfChip label="All" active={!status} onClick={() => setStatus('')} count={allQuery.isLoading ? undefined : countOf(() => true)} />
            {statuses.map((s) => (
              <PfChip key={s.value} label={s.label} active={status === s.value} onClick={() => setStatus(s.value)} count={allQuery.isLoading ? undefined : countOf((t) => t.status === s.value)} />
            ))}
          </PfChipRow>
          <PfPanel eyebrow="Ticket register" title="Field tickets" testId="pf-ticket-register" footer={!listQuery.isLoading && tickets.length ? `${tickets.length} ticket${tickets.length === 1 ? '' : 's'}` : undefined}>
            <PfRegister
              query={listQuery}
              rows={tickets}
              columns={columns}
              rail={(t) => pfTicketStatusSkin(t.status).rail}
              onOpen={(t) => openRecord(`/pipeline-facility/tickets/${t.id}`)}
              subject="field tickets"
              testId="pf-ticket"
              minWidth="min-w-[860px]"
              empty={status ? <PfEmpty>No ticket has this status.</PfEmpty> : <PfEmpty>No field tickets yet. Draft one from a job.</PfEmpty>}
              card={(t) => (
                <>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate font-mono text-sm font-extrabold text-white">{t.ticketNumber || '—'}</p>
                      <p className="mt-0.5 text-xs text-fieldiq-muted">{t.jobNumber || '—'} · {t.customerName || '—'}</p>
                    </div>
                    <PfBadge skin={pfTicketStatusSkin(t.status)} value={t.status} />
                  </div>
                  <p className="mt-2 text-xs text-fieldiq-text">
                    {pfScopeText(t)} · Total {pfTicketTotalText(t)}
                  </p>
                </>
              )}
            />
          </PfPanel>
        </div>
      </section>
    </div>
  );
}
